import { FC, ReactNode, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Grid,
  useTheme,
} from '@mui/material';
import { useResponsive } from 'src/hooks/useResponsiveHook';
import { ArrowForward } from '@mui/icons-material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useNavigate } from 'react-router-dom';

export interface SummaryCardData {
  id: string;
  title: string;
  value: string | number;
  icon?: ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  trend?: {
    value: number;
    direction: 'up' | 'down';
  };
  navigationPath?: string;
  subtitle?: string;
  isCritical?: boolean; // Show on mobile even if maxMobileCards is limited
}

interface SummaryCardProps {
  data: SummaryCardData;
  dataTestId?: string;
  onClick?: () => void;
}

export const SummaryCard: FC<SummaryCardProps> = ({
  data,
  dataTestId = 'summary-card',
  onClick,
}) => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const colorMap = {
    primary: theme.palette.primary,
    success: theme.palette.success,
    warning: theme.palette.warning,
    error: theme.palette.error,
    info: theme.palette.info,
  };

  const color = colorMap[data.color || 'primary'];

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (data.navigationPath) {
      navigate(data.navigationPath);
    }
  };

  const isClickable = !!onClick || !!data.navigationPath;

  return (
    <Card
      data-testid={dataTestId}
      onClick={isClickable ? handleClick : undefined}
      sx={{
        background: `linear-gradient(135deg, ${color.light}15 0%, ${color.light}05 100%)`,
        border: `1px solid ${color.light}40`,
        borderRadius: 2,
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: isClickable ? 'pointer' : 'default',
        height: '100%',
        '&:hover': isClickable
          ? {
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${color.light}30`,
          }
          : {},
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          {data.icon && (
            <Box
              sx={{
                color: color.main,
                fontSize: { xs: 24, sm: 28, md: 32 },
              }}
            >
              {data.icon}
            </Box>
          )}
          {data.trend && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: data.trend.direction === 'up' ? theme.palette.success.main : theme.palette.error.main,
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
              >
                {data.trend.direction === 'up' ? '↑' : '↓'} {Math.abs(data.trend.value)}%
              </Typography>
            </Box>
          )}
        </Box>
        <Typography
          color="textSecondary"
          gutterBottom
          variant="body2"
          fontWeight={500}
          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        >
          {data.title}
        </Typography>
        <Typography
          variant="h4"
          fontWeight={600}
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
          color={color.main}
        >
          {data.value}
        </Typography>
        {data.subtitle && (
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mt: 0.5, display: 'block' }}
          >
            {data.subtitle}
          </Typography>
        )}
        {isClickable && (
          <Box mt={1.5}>
            <Button
              size="small"
              endIcon={<ArrowForward />}
              sx={{
                color: color.main,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: color.light + '20',
                },
              }}
              data-testid={`${dataTestId}-cta`}
            >
              {translateText('common.viewDetails')}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

interface SummaryCardsProps {
  cards: SummaryCardData[];
  columns?: { xs?: number; sm?: number; md?: number; lg?: number };
  dataTestId?: string;
  maxMobileCards?: number; // Maximum cards to show on mobile (default: show all critical + first 2)
}

export const SummaryCards: FC<SummaryCardsProps> = ({
  cards,
  columns = { xs: 12, sm: 6, md: 4 },
  dataTestId = 'summary-cards',
  maxMobileCards,
}) => {
  const { isMobile } = useResponsive();

  // Filter cards for mobile: show critical cards + limit others
  // Must be called before any early returns (React hooks rule)
  const displayCards = useMemo(() => {
    if (!cards || cards.length === 0) {
      return [];
    }

    if (!isMobile || maxMobileCards === undefined) {
      return cards;
    }

    // Separate critical and non-critical cards
    const criticalCards = cards.filter((card) => card.isCritical);
    const nonCriticalCards = cards.filter((card) => !card.isCritical);

    // Show all critical cards + up to maxMobileCards non-critical cards
    const limitedNonCritical = nonCriticalCards.slice(0, Math.max(0, maxMobileCards - criticalCards.length));

    return [...criticalCards, ...limitedNonCritical];
  }, [cards, isMobile, maxMobileCards]);

  if (!cards || cards.length === 0 || displayCards.length === 0) {
    return null;
  }

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2, md: 3 }}
      sx={{ ml: '-0.75rem !important', width: 'calc(100% + 0.75rem) !important' }}
      data-testid={dataTestId}
    >
      {displayCards.map((card) => (
        <Grid item key={card.id} xs={columns.xs} sm={columns.sm} md={columns.md} lg={columns.lg}>
          <SummaryCard
            data={card}
            dataTestId={`${dataTestId}-${card.id}`}
          />
        </Grid>
      ))}
    </Grid>
  );
};
